import { useCallback, useEffect, useState } from 'react';

/**
 * Ladicí překryv rozvržení — zapíná se `?ladit=rozvrzeni` v adrese.
 *
 * Vznikl z konkrétní bolesti: pod Receptářem na telefonu zůstával pruh pozadí
 * a z fotky displeje se nedalo poznat, jestli je to barva, nebo chybějící
 * výška — odhadovat pixely z JPEGu vedlo dvakrát vedle. Emulace mobilu
 * v prohlížeči na to nestačí: `env(safe-area-inset-*)` je tam nula a WKWebView
 * se chová po svém. Jediný spolehlivý zdroj je skutečný telefon, jen z něj
 * potřebujeme **čísla, ne dojem**.
 *
 * Co ukazuje:
 *  - safe-area insety (měřené sondou, ne dopočítané)
 *  - výšky výřezu ze čtyř různých zdrojů — když se rozcházejí, je to ono
 *  - jestli běží instalace na ploše (`navigator.standalone`)
 *  - u každého prvku s `data-ladit="název"` jeho obdélník a hlavně
 *    **mezeru ke spodní hraně** — tj. přesně „dosahuje modul až dolů?"
 *
 * Prvek se do měření přihlásí sám:
 *     <div data-ladit="modul">…</div>
 *
 * Tlačítko „Kopírovat" dá text k vložení do chatu — rychlejší a přesnější
 * než screenshot.
 */

const sonda = (vlastnost) => {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;'
        + `width:${vlastnost};height:${vlastnost}`;
    document.body.appendChild(el);
    const r = el.getBoundingClientRect();
    el.remove();
    return { w: Math.round(r.width), h: Math.round(r.height) };
};

function zmer() {
    const vv = window.visualViewport;
    const docEl = document.documentElement;
    const bottomHrana = docEl.clientHeight;

    const prvky = [...document.querySelectorAll('[data-ladit]')].map(el => {
        const r = el.getBoundingClientRect();
        return {
            nazev: el.getAttribute('data-ladit'),
            top: Math.round(r.top),
            bottom: Math.round(r.bottom),
            vyska: Math.round(r.height),
            sirka: Math.round(r.width),
            mezeraDole: Math.round(bottomHrana - r.bottom),
            mezeraVpravo: Math.round(docEl.clientWidth - r.right),
        };
    });

    return {
        standalone: window.navigator.standalone === true,
        dpr: window.devicePixelRatio,
        safe: {
            top: sonda('env(safe-area-inset-top, 0px)').h,
            bottom: sonda('env(safe-area-inset-bottom, 0px)').h,
            left: sonda('env(safe-area-inset-left, 0px)').w,
            right: sonda('env(safe-area-inset-right, 0px)').w,
        },
        // Čtyři zdroje výšky. Shodují se? Pak je výřez v pořádku a případný
        // pruh dole je chyba rozvržení, ne prohlížeče.
        vyska: {
            innerHeight: window.innerHeight,
            clientHeight: docEl.clientHeight,
            visualViewport: vv ? Math.round(vv.height) : null,
            html100: Math.round(docEl.getBoundingClientRect().height),
        },
        hlavicka: getComputedStyle(docEl).getPropertyValue('--hlavicka-mobil').trim(),
        trida: docEl.className || '(žádná)',
        prvky,
    };
}

const naText = (d) => [
    `standalone=${d.standalone} dpr=${d.dpr} třídy=${d.trida}`,
    `safe: top=${d.safe.top} bottom=${d.safe.bottom} left=${d.safe.left} right=${d.safe.right}`,
    `výška: inner=${d.vyska.innerHeight} client=${d.vyska.clientHeight} visual=${d.vyska.visualViewport} html=${d.vyska.html100}`,
    `--hlavicka-mobil: ${d.hlavicka}`,
    ...d.prvky.map(p => `[${p.nazev}] top=${p.top} bottom=${p.bottom} v=${p.vyska} š=${p.sirka} mezeraDole=${p.mezeraDole} mezeraVpravo=${p.mezeraVpravo}`),
].join('\n');

export default function LaditRozvrzeni() {
    const [data, setData] = useState(null);
    const [sbaleno, setSbaleno] = useState(false);
    const [zprava, setZprava] = useState('');

    const prepocti = useCallback(() => setData(zmer()), []);

    useEffect(() => {
        prepocti();
        // WKWebView dosadí safe-area až chvíli po načtení, stejně jako
        // v `lib/iosInstalace.js` — jedno pozdní přeměření to podchytí.
        const t = setTimeout(prepocti, 700);
        window.addEventListener('resize', prepocti);
        window.addEventListener('orientationchange', prepocti);
        window.visualViewport?.addEventListener('resize', prepocti);
        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', prepocti);
            window.removeEventListener('orientationchange', prepocti);
            window.visualViewport?.removeEventListener('resize', prepocti);
        };
    }, [prepocti]);

    if (!data) return null;

    const radek = (k, v, zvyrazni = false) => (
        <div key={k} style={{ display: 'flex', gap: 6, color: zvyrazni ? '#FCD34D' : '#E5E7EB' }}>
            <span style={{ opacity: 0.6, flexShrink: 0 }}>{k}</span>
            <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
        </div>
    );

    const vysky = data.vyska;
    const rozchazi = new Set([vysky.innerHeight, vysky.clientHeight, vysky.visualViewport].filter(v => v != null)).size > 1;

    return (
        <div style={{
            position: 'fixed', zIndex: 2147483647,
            // Nad mobilní lištou, ať se nepřekrývají.
            left: 8, right: 8, bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
            maxHeight: '48vh', overflowY: 'auto',
            padding: '8px 10px', borderRadius: 10,
            background: 'rgba(8,8,12,0.94)', border: '1px solid rgba(255,255,255,0.18)',
            color: '#E5E7EB', font: '500 11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace',
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: sbaleno ? 0 : 6 }}>
                <strong style={{ color: '#FCD34D' }}>ladit: rozvržení</strong>
                <button onClick={prepocti} style={tlacitko}>Přeměřit</button>
                <button onClick={async () => {
                    const text = naText(data);
                    try { await navigator.clipboard.writeText(text); setZprava('zkopírováno'); }
                    catch { setZprava('schránka nejde — vyfoť'); }
                    setTimeout(() => setZprava(''), 1800);
                }} style={tlacitko}>Kopírovat</button>
                <button onClick={() => setSbaleno(s => !s)} style={{ ...tlacitko, marginLeft: 'auto' }}>
                    {sbaleno ? 'Rozbalit' : 'Sbalit'}
                </button>
            </div>
            {zprava && <div style={{ color: '#34D399', marginBottom: 4 }}>{zprava}</div>}

            {!sbaleno && (
                <>
                    {radek('standalone', String(data.standalone))}
                    {radek('dpr', data.dpr)}
                    {radek('safe t/b/l/r', `${data.safe.top} / ${data.safe.bottom} / ${data.safe.left} / ${data.safe.right}`)}
                    {radek('--hlavicka-mobil', data.hlavicka || '—')}
                    <div style={{ height: 1, background: 'rgba(255,255,255,0.12)', margin: '5px 0' }} />
                    {radek('inner / client', `${vysky.innerHeight} / ${vysky.clientHeight}`, rozchazi)}
                    {radek('visual / html', `${vysky.visualViewport ?? '—'} / ${vysky.html100}`, rozchazi)}
                    {rozchazi && <div style={{ color: '#FCA5A5' }}>výšky se rozcházejí → výřez není, co si myslíš</div>}
                    <div style={{ height: 1, background: 'rgba(255,255,255,0.12)', margin: '5px 0' }} />
                    {data.prvky.length === 0
                        ? <div style={{ opacity: 0.6 }}>žádný prvek s data-ladit</div>
                        : data.prvky.map(p => (
                            <div key={p.nazev} style={{ marginBottom: 3 }}>
                                <div style={{ color: '#93C5FD' }}>{p.nazev}</div>
                                {radek('top / bottom', `${p.top} / ${p.bottom}`)}
                                {radek('š × v', `${p.sirka} × ${p.vyska}`)}
                                {radek('mezera dole', p.mezeraDole, p.mezeraDole !== 0)}
                                {radek('mezera vpravo', p.mezeraVpravo, p.mezeraVpravo !== 0)}
                            </div>
                        ))}
                </>
            )}
        </div>
    );
}

const tlacitko = {
    background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)',
    color: '#E5E7EB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
    font: 'inherit', fontSize: 10.5,
};
